export const DIMENSION_CATALOG = [
  { id: 'student', label: 'Student', helperText: 'Groups by student (email + name)' },
  { id: 'track', label: 'Track', helperText: 'Groups by tracks such as Grade 2 Addition, Grade 3 Multiplication, etc.' },
  { id: 'grade', label: 'Grade', helperText: "Student's current knowledge grade" },
  { id: 'campus', label: 'Campus', helperText: 'Based on Timeback data' },
  { id: 'date', label: 'Date', helperText: 'Groups by event date (YYYY-MM-DD)' },
  { id: 'week', label: 'Week', helperText: 'Groups by week (starting Monday)' },
] as const;

export const METRIC_CATALOG = [
  { id: 'xpEarned', label: 'XP Earned', source: 'caliper' },
  { id: 'activeMinutes', label: 'Active Minutes', source: 'caliper' },
  { id: 'wasteMinutes', label: 'Waste Minutes', source: 'caliper' },
  { id: 'totalMinutes', label: 'Total Minutes', source: 'caliper' },
  { id: 'questionsAnswered', label: 'Questions Answered', source: 'caliper' },
  { id: 'questionsCorrect', label: 'Questions Correct', source: 'caliper' },
  { id: 'accuracyPct', label: 'Accuracy %', source: 'caliper' },
  { id: 'factsMastered', label: 'Facts Mastered', source: 'caliper' },
  { id: 'daysActive', label: 'Days Active', source: 'caliper' },
  { id: 'assessmentCount', label: 'Assessment Count', source: 'assessments' },
  { id: 'latestAssessmentCqpm', label: 'Latest Assessment CQPM', source: 'assessments' },
  { id: 'peakCqpm', label: 'Peak CQPM', source: 'assessments' },
  { id: 'latestAssessmentAccuracy', label: 'Latest Assessment Accuracy %', source: 'assessments' },
  { id: 'latestAssessmentDate', label: 'Latest Assessment Date', source: 'assessments' },
] as const;

export const DATE_FLOOR = '2025-08-01';

// Track options ordered by student progression sequence.
// PreK -> K -> Grade 1 -> Grade 2 -> Grade 3 -> Grade 4 -> Grade 5 -> ALL (Grade 5 mastered)
// TRACK1, TRACK2, TRACK3, TRACK4 are deprecated and excluded.
export const TRACK_OPTIONS = [
  // Pre-K (grade -1)
  { id: 'TRACK_PREK_NAMING', label: 'Pre-K Number Naming (0-9)' },
  { id: 'TRACK_PREK_SUBITIZING', label: 'Pre-K Subitizing (1-4)' },
  { id: 'TRACK_PREK_COUNTING', label: 'Pre-K Counting (1-5)' },
  { id: 'TRACK_PREK_WHATCOMESNEXT', label: 'Pre-K What Comes Next (3-9)' },
  // Kindergarten (grade 0)
  { id: 'TRACK_K_NAMING', label: 'K Number Naming (0-20)' },
  { id: 'TRACK_K_SUBITIZING', label: 'K Subitizing (1-6)' },
  { id: 'TRACK_K_COMPARING', label: 'K Comparing Numbers (1-10)' },
  { id: 'TRACK_K_PARTWHOLE', label: 'K Part Whole Within 5 (0-5)' },
  { id: 'TRACK_K_ADDITION_WITHIN_5', label: 'K Addition Within 5 (0-5)' },
  { id: 'TRACK_K_ACCURACY', label: 'K Addition Accuracy Assessment' },
  { id: 'TRACK13', label: 'Addition Within 10 (Kindergarten)' },
  // Grade 1
  { id: 'TRACK12', label: 'Addition Within 10 (Sums up to 10)' },
  // Grade 2
  { id: 'TRACK9', label: 'Addition (Single-Digit)' },
  { id: 'TRACK10', label: 'Subtraction (Single-Digit)' },
  // Grade 3
  { id: 'TRACK6', label: 'Addition Facts (Sums up to 20)' },
  { id: 'TRACK8', label: 'Subtraction Facts (Up to 20)' },
  { id: 'TRACK11', label: 'Multiplication (Single-digit)' },
  // Grade 4
  { id: 'TRACK7', label: 'Multiplication Facts (Factors up to 12)' },
  { id: 'TRACK5', label: 'Division Facts (Up to 12)' },
  { id: 'TRACK_FRACTION_IDENTIFICATION', label: 'Identifying Fractions' },
  // Grade 5 (L2 tracks)
  { id: 'TRACK6_L2', label: 'Addition Facts (Sums up to 20) - Grade 5' },
  { id: 'TRACK8_L2', label: 'Subtraction Facts (Up to 20) - Grade 5' },
  { id: 'TRACK7_L2', label: 'Multiplication Facts (Factors up to 12) - Grade 5' },
  { id: 'TRACK5_L2', label: 'Division Facts (Up to 12) - Grade 5' },
  // ALL track — Grade 5 mastered (last)
  { id: 'ALL', label: 'Grade 5 - all operations mastered' },
] as const;

export const GRADE_OPTIONS = [
  { value: -1, label: 'Pre-K' },
  { value: 0, label: 'Kindergarten' },
  { value: 1, label: 'Grade 1' },
  { value: 2, label: 'Grade 2' },
  { value: 3, label: 'Grade 3' },
  { value: 4, label: 'Grade 4' },
  { value: 5, label: 'Grade 5' },
] as const;

// Campus options sourced from OneRoster primaryOrg.name via backfillCampusFromOneRoster.ts
// Re-run the script monthly to pick up new campuses, then update this list.
export const CAMPUS_OPTIONS: string[] = [
  '2 Hour Learning',
  '2 Hour Single User',
  'AASP - Allendale Aspire Academy',
  'AFES - Allendale Fairfax Elementary School',
  'AFMS - Allendale Fairfax Middle School',
  'Alpha',
  'Alpha Anywhere (homeschool)',
  'Alpha Anywhere Center',
  'Alpha Brownsville',
  'Alpha Denver',
  'Alpha High School',
  'Alpha Internal Test School',
  'Alpha K-8',
  'Alpha Miami Summer School 2025',
  'Alpha School',
  'Alpha School Austin',
  'Alpha School Brownsville',
  'Alpha School Chantilly',
  'Alpha School Charlotte',
  'Alpha School Fort Worth',
  'Alpha School Houston',
  'Alpha School Lake Forest',
  'Alpha School Legacy',
  'Alpha School Miami',
  'Alpha School Orlando',
  'Alpha School Palm Beach',
  'Alpha School Plano',
  'Alpha School Raleigh',
  'Alpha School San Francisco',
  'Alpha School Santa Barbara',
  'Alpha School Scottsdale',
  'Alpha School Tampa',
  'Centner Academy',
  'Colearn Academy',
  'GT Anywhere',
  'GT School',
  'Guide School',
  'Havruta Learning Pod: Austin TX',
  'High School SAT Prep',
  'Homeschool',
  'ISEE Prep',
  'Immaculate Academy',
  'Irvington High School',
  'JHES - Hardeeville Elementary School',
  'JRES - Ridgeland Elementary School',
  'Kairos Learning Solutions',
  'LFG Academy',
  'Learn and Earn Poland',
  'Limitless Education - Malawi',
  'Lipscomb Academy Accelerate',
  'Living Water Elementary',
  'L\u00e9man Manhattan Preparatory School',
  'Metro Schools',
  'Modern Samurai Academy',
  'Montessorium Cedar Park',
  'NextGen Academy',
  'NextGen Afterschool Tutoring Austin',
  'Nova Academy Austin',
  'Nova Academy Bastrop',
  'Nova Bastrop',
  'Novatio',
  'One Hope Academy',
  'Oregon Trail School District 46',
  'Reading Community City School District',
  'SPIRE Academy',
  'ScienceSIS',
  'Strata',
  'Superbuilders UI Org',
  'TSA AIE Elite Prep',
  'TSA All American Prep',
  'TSA Baseball - Dallas',
  'TSA Baseball - Pearland',
  'TSA Basketball - Houston',
  'TSA DeepWater Prep',
  'TSA Dynamic Prep',
  'TSA Football - Carrollton',
  'TSA Gymnastics - San Antonio',
  'TSA Modern Samurai Academy',
  'TSA Online',
  'TSA The Bennett School',
  'Texas Prep School',
  'Texas Sports Academy',
  'Texas Sports Academy Baseball \u2013 Dallas',
  'Texas Sports Academy Online',
  'The St. James Performance Academy',
  'Unbound Academy',
  'Valenta Academy',
  'Vita High School',
  'Waypoint Academy',
];

export const DATA_CAVEAT_START = '2025-08-01';
export const DATA_CAVEAT_END = '2025-08-31';
